/** Align with tasks/vex/frontend/api-spec.md */

export const VEX_SCAN_TOOL = Object.freeze({
  CLARITY: "clarity",
  FOSSID: "fossid",
  CLARITYAIR: "clarityair",
  SBOM: "sbom",
});

/**
 * Map a `verificationType` label from the verification list APIs
 * ("Clarity", "FossId", "ClarityAIR", "clarity_air", "SBOM") to a GET /vex scanToolCd.
 */
export const resolveVexScanToolCd = (verificationType) => {
  const normalized = String(verificationType || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  switch (normalized) {
    case "clarity":
      return VEX_SCAN_TOOL.CLARITY;
    case "fossid":
      return VEX_SCAN_TOOL.FOSSID;
    case "clarityair":
      return VEX_SCAN_TOOL.CLARITYAIR;
    case "sbom":
      return VEX_SCAN_TOOL.SBOM;
    default:
      return null;
  }
};

/** Frozen VEX snapshot sources (not live GET /vex) */
export const VEX_SNAPSHOT_SOURCE = Object.freeze({
  VERIFICATION_REPORT: "verification-report",
  MERGED_SBOM: "merged-sbom",
  PRODUCT: "product",
});

/** File upload target for each frozen snapshot source. */
export const VEX_SNAPSHOT_ATTACHMENT_TARGET = Object.freeze({
  [VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT]: "VERIFICATION_REPORT_VEX",
  [VEX_SNAPSHOT_SOURCE.MERGED_SBOM]: "MERGED_SBOM_VEX",
  [VEX_SNAPSHOT_SOURCE.PRODUCT]: "MERGED_PRODUCT_VEX",
});

/** Primary key field per scanToolCd (verification list / import) */
export const VEX_VERIFICATION_SEQ_FIELD = Object.freeze({
  [VEX_SCAN_TOOL.CLARITY]: "binaryVerificationSeq",
  [VEX_SCAN_TOOL.FOSSID]: "sourceCodeVerificationSeq",
  [VEX_SCAN_TOOL.CLARITYAIR]: "clarityAirVerificationSeq",
  [VEX_SCAN_TOOL.SBOM]: "sbomVerificationSeq",
});

export const VEX_STATUS = Object.freeze({
  UNDER_INVESTIGATION: "UNDER_INVESTIGATION",
  AFFECTED: "AFFECTED",
  NOT_AFFECTED: "NOT_AFFECTED",
  FIXED: "FIXED",
});

/**
 * Live VEX may be edited when Ready (DONE) for any scan tool.
 * SBOM verification also allows edit during Review (ON_REVIEW).
 */
export const isVexEditableVerificationStatus = (statusCd, scanToolCd) => {
  if (statusCd === "DONE") return true;
  if (statusCd === "ON_REVIEW" && String(scanToolCd || "").toLowerCase() === VEX_SCAN_TOOL.SBOM) {
    return true;
  }
  return false;
};

/** Higher number = more severe when merging duplicate component/CVE VEX rows */
export const vexStatusSeverity = (status) => {
  switch (status) {
    case VEX_STATUS.AFFECTED:
      return 4;
    case VEX_STATUS.UNDER_INVESTIGATION:
      return 3;
    case VEX_STATUS.FIXED:
      return 2;
    case VEX_STATUS.NOT_AFFECTED:
      return 1;
    default:
      return 0;
  }
};

/** True when candidate should replace existing (higher severity, then newer updatedAt) */
export const shouldPreferVex = (existing, candidate) => {
  if (!existing) return Boolean(candidate);
  if (!candidate) return false;
  const existingSeverity = vexStatusSeverity(existing?.vexStatus);
  const candidateSeverity = vexStatusSeverity(candidate?.vexStatus);
  if (candidateSeverity !== existingSeverity) {
    return candidateSeverity > existingSeverity;
  }
  const existingTs = Date.parse(existing?.updatedAt || existing?.createdAt || "") || 0;
  const candidateTs = Date.parse(candidate?.updatedAt || candidate?.createdAt || "") || 0;
  return candidateTs > existingTs;
};

/** Merge VEX rows for the same CVE, keeping the most severe status */
export const mergeVexListByCve = (list) => {
  const map = new Map();
  if (!Array.isArray(list)) return [];
  list.forEach((vex) => {
    const key = vexListKey(vex?.cve, vex?.cvssVersion);
    if (!key || key === "|") return;
    const existing = map.get(key);
    if (!existing || shouldPreferVex(existing, vex)) {
      map.set(key, vex);
    }
  });
  return Array.from(map.values());
};

/**
 * All live VEX contexts for a row (primary verification + merged duplicates from deduplicateComponents).
 */
export const resolveRowVexSourceContexts = (row, resolveRowVexContext) => {
  if (!row) return [];
  const contexts = Array.isArray(row.vexSourceContexts) ? [...row.vexSourceContexts] : [];
  const primary = resolveRowVexContext?.(row);
  if (primary?.verificationSeq != null) {
    const primaryKey = vexContextKey({
      scanToolCd: primary.scanToolCd ?? resolveVexScanToolCd(row?.verificationType),
      verificationSeq: primary.verificationSeq,
    });
    const hasPrimary = contexts.some((ctx) => {
      const scanToolCd = ctx.scanToolCd ?? resolveVexScanToolCd(ctx.verificationType);
      return vexContextKey({ scanToolCd, verificationSeq: ctx.verificationSeq }) === primaryKey;
    });
    if (!hasPrimary) {
      contexts.unshift({
        verificationSeq: primary.verificationSeq,
        verificationType: primary.verificationType ?? row?.verificationType,
        scanToolCd: primary.scanToolCd,
        projectManagerId: primary.projectManagerId,
        verificationStatusCd: primary.verificationStatusCd,
      });
    }
  }
  return contexts
    .map((ctx) => ({
      ...ctx,
      scanToolCd: ctx.scanToolCd ?? resolveVexScanToolCd(ctx.verificationType ?? row?.verificationType),
    }))
    .filter((ctx) => vexContextKey(ctx));
};

export const VEX_STATUS_OPTIONS = [
  { value: VEX_STATUS.UNDER_INVESTIGATION, label: "Under Investigation" },
  { value: VEX_STATUS.AFFECTED, label: "Affected" },
  { value: VEX_STATUS.NOT_AFFECTED, label: "Not Affected" },
  { value: VEX_STATUS.FIXED, label: "Fixed" },
];

export const VEX_JUSTIFICATION = Object.freeze({
  COMPONENT_NOT_PRESENT: "component_not_present",
  VULNERABLE_CODE_NOT_PRESENT: "vulnerable_code_not_present",
  VULNERABLE_CODE_NOT_IN_EXECUTE_PATH: "vulnerable_code_not_in_execute_path",
  VULNERABLE_CODE_CANNOT_BE_CONTROLLED_BY_ADVERSARY: "vulnerable_code_cannot_be_controlled_by_adversary",
  INLINE_MITIGATIONS_ALREADY_EXIST: "inline_mitigations_already_exist",
});

/** Select options — value = API code; label = EN fallback (overridden by i18n in UI) */
export const VEX_JUSTIFICATION_OPTIONS = [
  { value: VEX_JUSTIFICATION.COMPONENT_NOT_PRESENT, label: "Component not present" },
  { value: VEX_JUSTIFICATION.VULNERABLE_CODE_NOT_PRESENT, label: "Vulnerable code not present" },
  {
    value: VEX_JUSTIFICATION.VULNERABLE_CODE_NOT_IN_EXECUTE_PATH,
    label: "Vulnerable code not in execute path",
  },
  {
    value: VEX_JUSTIFICATION.VULNERABLE_CODE_CANNOT_BE_CONTROLLED_BY_ADVERSARY,
    label: "Vulnerable code cannot be controlled by adversary",
  },
  {
    value: VEX_JUSTIFICATION.INLINE_MITIGATIONS_ALREADY_EXIST,
    label: "Inline mitigations already exist",
  },
];

export const VEX_ERROR_CODE = Object.freeze({
  FEATURE_DISABLED: "20700",
  NOT_FOUND: "20701",
  INVALID_STATUS: "20702",
  NOT_AFFECTED_WITHOUT_JUSTIFICATION: "20703",
  INVALID_JUSTIFICATION: "20704",
  INVALID_SCAN_TOOL: "20705",
});

export const vexListKey = (cve, cvssVersion) =>
  `${String(cve || "").trim()}|${String(cvssVersion || "").trim()}`;

/** Match key for per-component VEX progress on ComponentTable */
export const vexComponentKey = (componentName, componentVersion) =>
  `${String(componentName || "").trim()}|${String(componentVersion ?? "").trim()}`;

/**
 * Identity of a live VEX context (scan tool + verification).
 * Returns null when the context cannot address GET /vex.
 */
export const vexContextKey = (vexContext) => {
  if (!vexContext?.scanToolCd || vexContext?.verificationSeq == null) return null;
  return `${vexContext.scanToolCd}|${vexContext.verificationSeq}`;
};

/** processed = status is set and is not the default AFFECTED */
export const isVexProcessed = (vexStatus) =>
  Boolean(vexStatus && vexStatus !== VEX_STATUS.AFFECTED);

/**
 * Group GET /vex list into per-component progress.
 * processed = vexStatus !== AFFECTED
 */
export const buildComponentVexProgressMap = (list) => {
  const map = new Map();
  if (!Array.isArray(list)) return map;

  list.forEach((vex) => {
    const key = vexComponentKey(vex?.componentName, vex?.componentVersion);
    const entry = map.get(key) || { processed: 0, total: 0, underInvestigation: 0 };
    entry.total += 1;
    if (isVexProcessed(vex?.vexStatus)) {
      entry.processed += 1;
    }
    if (vex?.vexStatus === VEX_STATUS.UNDER_INVESTIGATION) {
      entry.underInvestigation += 1;
    }
    map.set(key, entry);
  });
  return map;
};

const emptyVexProgress = () => ({
  processed: 0,
  total: 0,
  affected: 0,
  notAffected: 0,
  fixed: 0,
  underInvestigation: 0,
});

export const countVexStatusBreakdown = (list) => {
  const counts = {
    affected: 0,
    notAffected: 0,
    fixed: 0,
    underInvestigation: 0,
  };

  if (!Array.isArray(list)) return counts;

  list.forEach((vex) => {
    switch (vex?.vexStatus) {
      case VEX_STATUS.AFFECTED:
        counts.affected += 1;
        break;
      case VEX_STATUS.NOT_AFFECTED:
        counts.notAffected += 1;
        break;
      case VEX_STATUS.FIXED:
        counts.fixed += 1;
        break;
      case VEX_STATUS.UNDER_INVESTIGATION:
        counts.underInvestigation += 1;
        break;
      default:
        break;
    }
  });

  return counts;
};

export const hasVexProgressBreakdown = (progress) =>
  Boolean(
    progress &&
      (progress.affected != null ||
        progress.notAffected != null ||
        progress.fixed != null ||
        progress.underInvestigation != null),
  );

/** Rows for inline / tooltip VEX status counts */
export const getVexProgressBreakdownItems = (progress, t) => {
  if (!hasVexProgressBreakdown(progress)) return [];

  return [
    { key: "affected", label: t("status.vex-status-affected"), count: progress.affected ?? 0 },
    { key: "notAffected", label: t("status.vex-status-not-affected"), count: progress.notAffected ?? 0 },
    { key: "fixed", label: t("status.vex-status-fixed"), count: progress.fixed ?? 0 },
    {
      key: "underInvestigation",
      label: t("status.vex-status-under-investigation"),
      count: progress.underInvestigation ?? 0,
    },
  ];
};

/** Tooltip lines: Affected / Not Affected / Fixed / Under Investigation counts */
export const formatVexProgressBreakdownTooltip = (progress, t) => {
  const items = getVexProgressBreakdownItems(progress, t);
  if (!items.length) return null;

  return items.map((item) => `${item.label}: ${item.count}`).join("\n");
};

export const computeVexProgress = (list) => {
  if (!Array.isArray(list)) return emptyVexProgress();

  const deduped = mergeVexListByCve(list);
  const breakdown = countVexStatusBreakdown(deduped);
  const total = deduped.length;
  const processed = deduped.filter((vex) => isVexProcessed(vex?.vexStatus)).length;

  return { processed, total, ...breakdown };
};

/** Sum per-row progress objects (e.g. table header vs row badge totals). */
export const sumVexProgress = (progressList) => {
  const aggregated = emptyVexProgress();
  if (!Array.isArray(progressList)) return null;

  let hasEntries = false;
  progressList.forEach((progress) => {
    if (!progress || !progress.total) return;
    hasEntries = true;
    aggregated.processed += progress.processed ?? 0;
    aggregated.total += progress.total ?? 0;
    aggregated.affected += progress.affected ?? 0;
    aggregated.notAffected += progress.notAffected ?? 0;
    aggregated.fixed += progress.fixed ?? 0;
    aggregated.underInvestigation += progress.underInvestigation ?? 0;
  });

  return hasEntries ? aggregated : null;
};

/**
 * Group a frozen snapshot Vex[] by `componentSeq` — the row PK of the snapshot source.
 * Keys are stringified so number/string PKs from row vs API both match.
 */
export const buildSnapshotVexProgressMap = (list) => {
  const map = new Map();
  if (!Array.isArray(list)) return map;

  const byComponentSeq = new Map();
  const byIdentity = new Map();
  list.forEach((vex) => {
    if (vex?.componentSeq != null) {
      const key = String(vex.componentSeq);
      const entries = byComponentSeq.get(key) || [];
      entries.push(vex);
      byComponentSeq.set(key, entries);
    }
    const identityKey = vexComponentKey(vex?.componentName, vex?.componentVersion);
    if (identityKey && identityKey !== "|") {
      const entries = byIdentity.get(identityKey) || [];
      entries.push(vex);
      byIdentity.set(identityKey, entries);
    }
  });

  const setProgress = (key, entries) => {
    if (!key || !entries?.length) return;
    map.set(key, computeVexProgress(mergeVexListByCve(entries)));
  };

  byComponentSeq.forEach((entries, key) => setProgress(key, entries));
  byIdentity.forEach((entries, key) => setProgress(key, entries));
  return map;
};

export const VEX_SECURITIES_BADGE_STATE = Object.freeze({
  INCOMPLETE: "incomplete",
  UNDER_INVESTIGATION: "underInvestigation",
  COMPLETE: "complete",
});

/**
 * Badge color state for per-component VEX progress.
 * - incomplete (red): processed < total
 * - underInvestigation (orange): processed === total but some CVEs remain Under Investigation
 * - complete (green): processed === total with no Under Investigation
 */
export const getVexSecuritiesBadgeState = (progress) => {
  if (!progress || progress.total <= 0) return null;

  const processed = progress.processed ?? 0;
  const total = progress.total ?? 0;
  const underInvestigation = progress.underInvestigation ?? 0;

  if (processed < total) return VEX_SECURITIES_BADGE_STATE.INCOMPLETE;
  if (underInvestigation > 0) return VEX_SECURITIES_BADGE_STATE.UNDER_INVESTIGATION;
  return VEX_SECURITIES_BADGE_STATE.COMPLETE;
};

export const hasFrozenSnapshotParentContext = (frozenVexSnapshot) => {
  if (!frozenVexSnapshot?.source) return false;
  switch (frozenVexSnapshot.source) {
    case VEX_SNAPSHOT_SOURCE.PRODUCT:
      return frozenVexSnapshot.productSeq != null;
    case VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT:
      return frozenVexSnapshot.verificationReportSeq != null;
    case VEX_SNAPSHOT_SOURCE.MERGED_SBOM:
      return frozenVexSnapshot.mergedSbomSeq != null;
    default:
      return false;
  }
};

/**
 * Product / group frozen VEX descriptor for GET /vex/product.
 * - Product merge: productSeq only
 * - Named group: filterByGroupProduct + groupProductSeq
 * - Default group: filterByGroupProduct, omit groupProductSeq
 */
export const buildProductFrozenVexSnapshot = ({
  productSeq,
  isFromGroup = false,
  groupProductSeq = null,
} = {}) => {
  if (productSeq == null) return null;
  if (!isFromGroup) {
    return { source: VEX_SNAPSHOT_SOURCE.PRODUCT, productSeq };
  }
  const snapshot = {
    source: VEX_SNAPSHOT_SOURCE.PRODUCT,
    productSeq,
    filterByGroupProduct: true,
  };
  if (groupProductSeq != null && groupProductSeq !== "") {
    snapshot.groupProductSeq = groupProductSeq;
  }
  return snapshot;
};

/** Row PK field carried by a component row, per snapshot source */
export const VEX_SNAPSHOT_COMPONENT_SEQ_FIELD = Object.freeze({
  [VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT]: "verificationReportDetailSeq",
  [VEX_SNAPSHOT_SOURCE.MERGED_SBOM]: "mergedSbomComponentSeq",
  [VEX_SNAPSHOT_SOURCE.PRODUCT]: "mergedProductComponentSeq",
});

/** Read the snapshot row PK off a component row; null when the row does not carry it */
export const resolveSnapshotComponentSeq = (row, snapshotSource) => {
  const field = VEX_SNAPSHOT_COMPONENT_SEQ_FIELD[snapshotSource];
  if (!field) return null;
  return row?.[field] ?? null;
};
